import React, { useEffect, useState, useContext } from 'react';
import { dbPost } from '../../apis/commonApi';
import DatePicker from '../../components/dateComponents/DatePicker2';
import GbitTablePaging from '../../components/tableComponents/GbitTablePaging';
import { formatDateToISOString } from '../../services/UtilService';
import { ModalContext } from '../../components/contexts';

const PageTitle = () => (
  <div className="page-title-wrap" data-type="responsive">
    <h2 className="h-tit">접속 로그 조회</h2>
  </div>
);

const DateFilter = ({
  onDateChange,
  customStartDate,
  customEndDate,
  setCustomStartDate,
  setCustomEndDate,
  userId,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('전체');
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const { modalService } = useContext(ModalContext);
  const [displayStartDate, setDisplayStartDate] = useState('');
  const [displayEndDate, setDisplayEndDate] = useState('');

  const formatDateForDisplay = date => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateDates = (date, comparisonDate, type) => {
    if (type === 'endDate' && !comparisonDate) {
      modalService.errorAlert('먼저 시작 날짜를 선택하세요.');
      return false;
    }

    const selectedDate = new Date(date);
    const compareDate = comparisonDate ? new Date(comparisonDate) : null;

    if (type === 'startDate' && compareDate && selectedDate > compareDate) {
      modalService.errorAlert('시작 날짜는 종료 날짜보다 이후일 수 없습니다.');
      return false;
    }

    if (type === 'endDate' && compareDate && selectedDate < compareDate) {
      modalService.errorAlert('종료 날짜는 시작 날짜보다 이전일 수 없습니다.');
      return false;
    }

    return true;
  };

  const handleStartDateChange = date => {
    const formattedDate = formatDateToISOString(date);
    const displayDate = formatDateForDisplay(date);
    if (!validateDates(formattedDate, customEndDate, 'startDate')) {
      return;
    }
    setCustomStartDate(formattedDate);
    onDateChange({ startDate: formattedDate, endDate: customEndDate });
    setDisplayStartDate(displayDate);
  };

  const handleEndDateChange = date => {
    const formattedDate = formatDateToISOString(date);
    const displayDate = formatDateForDisplay(date);
    if (!validateDates(formattedDate, customStartDate, 'endDate')) {
      return;
    }
    setCustomEndDate(formattedDate);
    onDateChange({ startDate: customStartDate, endDate: formattedDate });
    setDisplayEndDate(displayDate);
  };

  const handlePeriodSelect = label => {
    setSelectedPeriod(label);
    if (label !== '기간 조회') {
      const today = new Date();
      let startDate = null;
      let endDate = new Date();

      if (label === '3개월') {
        startDate = new Date(today.setMonth(today.getMonth() - 3));
      } else if (label === '6개월') {
        startDate = new Date(today.setMonth(today.getMonth() - 6));
      } else if (label === '1년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 1));
      } else if (label === '2년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 2));
      } else if (label === '3년') {
        startDate = new Date(today.setFullYear(today.getFullYear() - 3));
      } else if (label === '전체') {
        startDate = null;
        endDate = null;
      }

      const formattedStartDate = formatDateToISOString(startDate);
      const formattedEndDate = formatDateToISOString(endDate, true);

      onDateChange({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        userId,
      });
    }
  };

  return (
    <td>
      <div className="input-group">
        {['전체', '3개월', '6개월', '1년', '2년', '3년', '기간 조회'].map(
          (label, idx) => (
            <div className="form-chip sm" key={idx}>
              <input
                type="radio"
                className="radio"
                name="rdo_chip_period"
                id={`rdo_chip_period_${idx}`}
                checked={selectedPeriod === label}
                onChange={() => handlePeriodSelect(label)}
              />
              <label
                className="form-chip-outline"
                htmlFor={`rdo_chip_period_${idx}`}
                style={{ height: 'auto' }}
              >
                {label}
              </label>
            </div>
          )
        )}
      </div>
      {selectedPeriod === '기간 조회' && (
        <div className="input-group gap-md">
          <DatePicker
            selectedDate={displayStartDate}
            onDateChange={handleStartDateChange}
            label="시작 날짜"
          />
          <DatePicker
            selectedDate={displayEndDate}
            onDateChange={handleEndDateChange}
            label="종료 날짜"
          />
        </div>
      )}
    </td>
  );
};

const UserFilter = ({ onUserChange }) => (
  <div className="input-group">
    <input
      type="text"
      className="form-control md"
      placeholder="사용자를 입력해 주세요"
      onChange={e => onUserChange(e.target.value)}
      aria-labelledby="userNm"
      id="userNm"
    />
    <label className="hidden-label" htmlFor="userNm">
      라벨
    </label>
  </div>
);

const LogTable = ({ logData, currentPage, itemsPerPage }) => (
  <div className="tbl-wrap">
    <h3 className="block-tit">로그 내역</h3>
    {logData.length === 0 ? (
      <div className="no-data">조회 결과가 없습니다.</div>
    ) : (
      <table className="tbl col data scroll">
        <caption>접근 로그 내역 조회 결과 테이블입니다.</caption>
        <colgroup>
          <col style={{ width: '8rem' }} />
          <col style={{ width: '12rem' }} />
          <col style={{ width: '30rem' }} />
          <col style={{ width: '15rem' }} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">순번</th>
            <th scope="col">사용자아이디</th>
            <th scope="col">접속 URL</th>
            <th scope="col">접속 시간</th>
          </tr>
        </thead>
        <tbody>
          {logData.map((log, idx) => (
            <tr key={idx}>
              <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
              <td>{log.userId}</td>
              <td>
                <strong>{log.urlPath}</strong>
              </td>
              <td>{log.cntnDt}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const AccessLog = () => {
  useEffect(() => {
    document.title = '통계 - 접속 로그 조회';
  }, []);
  const [logData, setLogData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filter, setFilter] = useState({
    startDate: null,
    endDate: null,
    userId: '',
    currentPage,
    pageSize: itemsPerPage,
  });
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [userIdInput, setUserIdInput] = useState('');
  const [totalCnt, setTotalCnt] = useState(0);

  const fetchLogData = async (params = filter) => {
    try {
      const countResponse = await dbPost('/stats/accessLog/count.do', params);
      setTotalCnt(countResponse || 1);

      const response = await dbPost('/stats/accessLog/search.do', params);
      setLogData(response || []);
    } catch (error) {}
  };

  useEffect(() => {
    fetchLogData(filter);
  }, [filter]);

  const handlePageChange = page => {
    const pageNumber = Number(page);
    setCurrentPage(pageNumber);
    setFilter(prev => ({ ...prev, currentPage: pageNumber }));
  };

  const paginatedData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return logData.slice(startIndex, startIndex + itemsPerPage);
  };

  const handleFilterChange = updatedFilter => {
    setFilter(prev => ({ ...prev, ...updatedFilter }));
  };

  const handleSearch = () => {
    const { startDate, endDate } = filter;
    setCurrentPage(1);
    const updatedFilter = {
      userId: userIdInput,
      startDate: formatDateToISOString(customStartDate) || startDate,
      endDate: formatDateToISOString(customEndDate, true) || endDate,
      currentPage: 1,
    };
    setCurrentPage(1);
    setFilter(prev => ({ ...prev, ...updatedFilter }));
  };

  return (
    <div className="contents">
      <PageTitle />
      <div className="tbl-wrap">
        <table className="tbl data row">
          <caption>조회 기간 선택 테이블입니다.</caption>
          <tbody>
            <tr>
              <th scope="row">조회 기간</th>
              <DateFilter
                onDateChange={handleFilterChange}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                setCustomStartDate={setCustomStartDate}
                setCustomEndDate={setCustomEndDate}
                userId={userIdInput}
              />
            </tr>
            <tr>
              <th scope="row">사용자</th>
              <td>
                <UserFilter onUserChange={userId => setUserIdInput(userId)} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="page-btn-wrap gap-lg">
        <button
          type="button"
          className="btn xlg primary"
          onClick={handleSearch}
        >
          검색하기
        </button>
      </div>
      <LogTable
        logData={logData}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
      />
      <GbitTablePaging
        totalItems={totalCnt}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default AccessLog;
